/**
 * Collections Showcase Component
 * Gère l'interaction entre le slider de collections et l'affichage des produits
 * Style Sova : Utilise la barre de défilement native
 */

class CollectionsShowcase {
  constructor(element) {
    this.element = element;
    this.sectionId = element.dataset.sectionId;
    this.collectionsSlider = element;
    this.productsContainer = element.closest('.collections-showcase').querySelector('.products-container');
    this.collectionTabs = element.querySelectorAll('[data-collection-button]');
    this.productSliders = element.closest('.collections-showcase').querySelectorAll('.products-slider');
    this.activeCollectionId = null;
    this.isLoading = false;

    this.init();
  }

  init() {
    console.log('Initializing CollectionsShowcase');
    console.log('Elements found:', {
      collectionsSlider: !!this.collectionsSlider,
      productsContainer: !!this.productsContainer,
      collectionTabs: this.collectionTabs.length,
      productSliders: this.productSliders.length
    });
    
    this.bindEvents();
    this.setInitialActiveCollection();
  }

  bindEvents() {
    console.log('Binding events to', this.collectionTabs.length, 'tabs');
    
    // Écouter les clics sur les onglets de collection
    this.collectionTabs.forEach((tab, index) => {
      console.log(`Binding click event to tab ${index}:`, tab.dataset.collectionId);
      tab.addEventListener('click', this.handleCollectionTabClick.bind(this));
    });

    // Écouter les événements du clavier pour l'accessibilité
    this.collectionTabs.forEach(tab => {
      tab.addEventListener('keydown', this.handleKeyDown.bind(this));
    });
  }

  handleCollectionTabClick(event) {
    const button = event.currentTarget;
    const collectionId = button.dataset.collectionId;
    const collectionHandle = button.dataset.collectionHandle;

    console.log('Collection tab clicked:', {
      collectionId,
      collectionHandle,
      activeCollectionId: this.activeCollectionId,
      isLoading: this.isLoading
    });

    if (this.isLoading || collectionId === this.activeCollectionId) {
      console.log('Ignoring click - loading or already active');
      return;
    }

    this.switchToCollection(collectionId, collectionHandle, button);
  }

  handleKeyDown(event) {
    // Support navigation au clavier (Entrée et Espace)
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleCollectionTabClick(event);
    }
  }

  setInitialActiveCollection() {
    console.log('Setting initial active collection');
    console.log('Collection tabs found:', this.collectionTabs.length);
    
    const firstTab = this.collectionTabs[0];
    if (firstTab) {
      const collectionId = firstTab.dataset.collectionId;
      console.log('First tab collection ID:', collectionId);
      
      this.activeCollectionId = collectionId;
      this.updateTabsState(firstTab);
      this.showProductsSlider(collectionId);
    } else {
      console.error('No collection tabs found');
    }
  }

  switchToCollection(collectionId, collectionHandle, clickedButton) {
    if (this.isLoading) return;

    this.isLoading = true;

    // Mettre à jour l'état des onglets
    this.updateTabsState(clickedButton);

    // Afficher le slider de produits correspondant
    this.showProductsSlider(collectionId);

    // Réduire le délai pour une transition plus fluide
    setTimeout(() => {
      this.isLoading = false;
      this.activeCollectionId = collectionId;
    }, 100);
  }

  updateTabsState(activeButton) {
    // Désactiver tous les onglets
    this.collectionTabs.forEach(tab => {
      tab.classList.remove('collection-tab__button--active');
      tab.setAttribute('aria-pressed', 'false');
      const wrapper = tab.closest('.collection-tab__wrapper');
      if (wrapper) {
        wrapper.classList.remove('collection-tab__wrapper--active');
      }
    });

    // Activer l'onglet cliqué
    activeButton.classList.add('collection-tab__button--active');
    activeButton.setAttribute('aria-pressed', 'true');
    const activeWrapper = activeButton.closest('.collection-tab__wrapper');
    if (activeWrapper) {
      activeWrapper.classList.add('collection-tab__wrapper--active');
    }
  }

  showProductsSlider(collectionId) {
    console.log('Switching to collection:', collectionId);
    console.log('Available product sliders:', this.productSliders.length);
    
    // Trouver le slider cible d'abord
    const targetSlider = this.productsContainer ? 
      this.productsContainer.querySelector(`[data-collection-id="${collectionId}"]`) : null;

    console.log('Target slider found:', !!targetSlider);

    if (targetSlider) {
      // Si le slider cible est déjà visible, ne rien faire
      if (!targetSlider.classList.contains('products-slider--hidden')) {
        return;
      }

      // Méthode améliorée : transition en fondu sans perte de hauteur
      // 1. Mesurer la hauteur actuelle du conteneur
      const containerHeight = this.productsContainer.offsetHeight;
      
      // 2. Fixer temporairement la hauteur pour éviter le "saut"
      this.productsContainer.style.height = `${containerHeight}px`;
      this.productsContainer.style.overflow = 'hidden';
      
      // 3. Masquer les autres sliders en fondu
      this.productSliders.forEach(slider => {
        if (slider !== targetSlider) {
          slider.style.transition = 'opacity 0.2s ease-out';
          slider.style.opacity = '0';
          slider.style.pointerEvents = 'none';
        }
      });

      // 4. Après le fondu, changer la visibilité et afficher le nouveau
      setTimeout(() => {
        // Masquer complètement les anciens sliders
        this.productSliders.forEach(slider => {
          if (slider !== targetSlider) {
            slider.classList.add('products-slider--hidden');
            slider.style.opacity = '';
            slider.style.transition = '';
            slider.style.pointerEvents = '';
          }
        });

        // Afficher le nouveau slider
        targetSlider.classList.remove('products-slider--hidden');
        targetSlider.style.opacity = '0';
        targetSlider.style.transition = 'opacity 0.2s ease-in';
        
        // Forcer le reflow
        targetSlider.offsetHeight;
        
        // Fondu d'entrée
        targetSlider.style.opacity = '1';
        
        // Laisser le conteneur retrouver sa hauteur naturelle
        setTimeout(() => {
          this.productsContainer.style.height = '';
          this.productsContainer.style.overflow = '';
          targetSlider.style.transition = '';
          targetSlider.style.opacity = '';
        }, 200);
        
      }, 200);

      console.log('Showing slider with smooth transition:', collectionId);
    } else {
      console.error('Target slider not found for collection:', collectionId);
    }
  }

  // Méthode publique pour changer de collection depuis l'extérieur
  setActiveCollection(collectionId) {
    const targetTab = Array.from(this.collectionTabs).find(
      tab => tab.dataset.collectionId === collectionId
    );

    if (targetTab) {
      this.switchToCollection(
        collectionId,
        targetTab.dataset.collectionHandle,
        targetTab
      );
    }
  }

  // Méthode pour récupérer la collection active
  getActiveCollection() {
    return this.activeCollectionId;
  }
}

// Auto-initialisation quand le DOM est prêt
document.addEventListener('DOMContentLoaded', function() {
  const collectionsSliders = document.querySelectorAll('.collections-slider[data-section-id]');
  
  collectionsSliders.forEach(slider => {
    new CollectionsShowcase(slider);
  });
});

// Rendre la classe disponible globalement pour les interactions externes
window.CollectionsShowcase = CollectionsShowcase;

// Support pour les changements de section dans l'éditeur Shopify
document.addEventListener('shopify:section:load', function(event) {
  const section = event.target;
  const collectionsSlider = section.querySelector('.collections-slider');
  
  if (collectionsSlider) {
    new CollectionsShowcase(collectionsSlider);
  }
});

document.addEventListener('shopify:section:unload', function(event) {
  // Nettoyage si nécessaire lors du déchargement de section
  const section = event.target;
  const collectionsSlider = section.querySelector('.collections-slider');
  
  if (collectionsSlider && collectionsSlider.collectionsShowcase) {
    // Nettoyer les événements si nécessaire
    delete collectionsSlider.collectionsShowcase;
  }
}); 